import { __decorate } from "tslib";
import { html, literal } from 'lit/static-html.js';
import { customElement, property, query, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { ifDefined } from 'lit-html/directives/if-defined.js';
import { localized } from '@lit/localize';
import { nothing } from 'lit-html';
import HelixElement from '../../shared/types/helix-element.js';
import { SlotController } from '../../shared/controllers/slot.controller.js';
import { shrinkWrappedText } from '../../shared/directives/shrink-wrapped-text.directive.js';
import { componentStyles } from '../../shared/styles/component.styles.js';
import { buttonStyles } from './button.styles.js';
import '../spinner/spinner.js';
import { findForm, submitForm } from '../../shared/forms/form-utils.js';
/**
 * @summary Button component to perform actions.
 * @documentation https://exas-helix-ds.exactsciences.net/index.html?path=/docs/components-button--docs
 *
 * @dependency hlx-spinner
 *
 * @event hlx-blur - Emitted when the button loses focus.
 * @event hlx-focus - Emitted when the button gains focus.
 *
 * @slot - The button's label.
 * @slot prefix - An icon to the left of the label.
 * @slot suffix - An icon to the right of the label.
 *
 * @csspart base - The base element.
 * @csspart prefix - The prefix slot container.
 * @csspart label - The label slot container.
 * @csspart suffix - The suffix slot container.
 * @csspart icon - The icon slot container.
 */
let HlxButton = class HlxButton extends HelixElement {
    constructor() {
        super(...arguments);
        this.type = 'button';
        this.disabled = false;
        this.loading = false;
        this.kind = 'primary';
        this.size = 'medium';
        this.variant = 'brand';
        this.title = '';
        this.form = '';
        this.prefixMotion = 'none';
        this.suffixMotion = 'none';
        this.target = '_self';
        this.hasFocus = false;
        this.labelText = '';
        this.slotController = new SlotController(this, 'prefix', 'suffix', 'icon');
        this.handleHostClick = (event) => {
            if (this.disabled || this.loading) {
                event.preventDefault();
                event.stopImmediatePropagation();
            }
        };
    }
    connectedCallback() {
        super.connectedCallback();
        this.addEventListener('click', this.handleHostClick);
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.removeEventListener('click', this.handleHostClick);
    }
    render() {
        const isLink = !!this.href;
        const tag = isLink ? literal `a` : literal `button`;
        return html `
            <${tag}
                part="base"
                type=${this.type}
                class="${classMap({
            'hlx-btn': true,
            'hlx-btn-primary': this.kind === 'primary',
            'hlx-btn-secondary': this.kind === 'secondary',
            'hlx-btn-tertiary': this.kind === 'tertiary',
            'hlx-btn-brand': this.variant === 'brand',
            'hlx-btn-dark': this.variant === 'dark',
            'hlx-btn-success': this.variant === 'success',
            'hlx-btn-error': this.variant === 'error',
            'hlx-btn-warning': this.variant === 'warning',
            'hlx-btn-info': this.variant === 'info',
            'hlx-btn-inverted': this.variant === 'inverted',
            'hlx-btn-sm': this.size === 'small',
            'hlx-btn-md': this.size === 'medium',
            'hlx-btn-icon-only': this.slotController.hasSlot('icon'),
            'hlx-btn-disabled': this.disabled || this.loading,
            'hlx-btn-focused': this.hasFocus,
            'hlx-btn-loading': this.loading,
        })}"
                title=${this.title}
                ?disabled=${this.disabled || this.loading}
                href=${ifDefined(isLink && !(this.disabled || this.loading) ? this.href : undefined)}
                target=${ifDefined(isLink ? this.target : undefined)}
                download=${ifDefined(isLink ? this.download : undefined)}
                rel=${ifDefined(isLink ?
            this.rel !== undefined ? this.rel
                : this.target === '_blank' ? 'noopener noreferrer'
                    : undefined
            : undefined)}
                @click=${this.handleClick}
                @blur=${this.handleBlur}
                @focus=${this.handleFocus}
                aria-disabled=${this.disabled || this.loading ? 'true' : 'false'}
                tabindex=${this.disabled || this.loading ? '-1' : '0'}>
                <slot name="icon" part="icon" class="hlx-btn-icon"></slot>
                <slot
                    name="prefix"
                    part="prefix"
                    class="${classMap({
            'hlx-btn-prefix': true,
            'hlx-animation-with-prefers-reduced-motion': true,
            'hlx-btn-prefix-slotted': this.slotController.hasSlot('prefix'),
            'hlx-btn-motion-up': this.prefixMotion === 'up',
            'hlx-btn-motion-up-left': this.prefixMotion === 'up-left',
            'hlx-btn-motion-left': this.prefixMotion === 'left',
            'hlx-btn-motion-down-left': this.prefixMotion === 'down-left',
            'hlx-btn-motion-down': this.prefixMotion === 'down',
        })}"></slot>
                    <span class="hlx-btn-label">
                    <slot
                        part="label"
                        class="hlx-btn-label-slot"
                        @slotchange=${this.handleLabelSlotChange}></slot>
                </span>
                <slot
                    name="suffix"
                    part="suffix"
                    class="${classMap({
            'hlx-btn-suffix': true,
            'hlx-animation-with-prefers-reduced-motion': true,
            'hlx-btn-suffix-slotted': this.slotController.hasSlot('suffix'),
            'hlx-btn-motion-up': this.suffixMotion === 'up',
            'hlx-btn-motion-up-right': this.suffixMotion === 'up-right',
            'hlx-btn-motion-right': this.suffixMotion === 'right',
            'hlx-btn-motion-down-right': this.suffixMotion === 'down-right',
            'hlx-btn-motion-down': this.suffixMotion === 'down',
        })}"></slot>
                ${this.loading ?
            html `
                            <span class="hlx-btn-loading-wrapper">
                                <hlx-spinner
                                    class="${classMap({
                'hlx-btn-loading-spinner': true,
                'hlx-btn-loading-spinner-margin': this.labelText,
            })}"></hlx-spinner>
                                ${this.labelText ?
                html `
                                        <span id="loading-text" class="hlx-btn-loading-text">
                                            ${this.labelText}
                                        </span>
                                    `
                : nothing}
                            </span>
                        `
            : nothing}
            </${tag}>
        `;
    }
    updated(changedProperties) {
        if (this.loading) {
            shrinkWrappedText(this.loadingTextEl);
        }
    }
    getForm() {
        return findForm(this);
    }
    handleClick() {
        if (this.type === 'submit') {
            submitForm(findForm(this));
        }
    }
    handleBlur() {
        this.hasFocus = false;
        this.emit('hlx-blur');
    }
    handleFocus() {
        this.hasFocus = true;
        this.emit('hlx-focus');
    }
    handleLabelSlotChange(e) {
        const childNodes = e.target.assignedNodes({ flatten: true });
        this.labelText = childNodes
            .map((node) => {
            return node.textContent ? node.textContent : '';
        })
            .join('');
    }
};
HlxButton.styles = [componentStyles, buttonStyles];
__decorate([
    property({ type: String })
], HlxButton.prototype, "type", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], HlxButton.prototype, "disabled", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], HlxButton.prototype, "loading", void 0);
__decorate([
    property({ type: String, reflect: true })
], HlxButton.prototype, "kind", void 0);
__decorate([
    property({ type: String, reflect: true })
], HlxButton.prototype, "size", void 0);
__decorate([
    property({ type: String, reflect: true })
], HlxButton.prototype, "variant", void 0);
__decorate([
    property({ type: String })
], HlxButton.prototype, "title", void 0);
__decorate([
    property({ type: String })
], HlxButton.prototype, "form", void 0);
__decorate([
    property({ type: String, attribute: 'prefix-motion' })
], HlxButton.prototype, "prefixMotion", void 0);
__decorate([
    property({ type: String, attribute: 'suffix-motion' })
], HlxButton.prototype, "suffixMotion", void 0);
__decorate([
    property({ type: String })
], HlxButton.prototype, "href", void 0);
__decorate([
    property({ type: String })
], HlxButton.prototype, "target", void 0);
__decorate([
    property({ type: String })
], HlxButton.prototype, "download", void 0);
__decorate([
    property({ type: String })
], HlxButton.prototype, "rel", void 0);
__decorate([
    query('#loading-text')
], HlxButton.prototype, "loadingTextEl", void 0);
__decorate([
    state()
], HlxButton.prototype, "hasFocus", void 0);
__decorate([
    state()
], HlxButton.prototype, "labelText", void 0);
HlxButton = __decorate([
    customElement('hlx-button'),
    localized()
], HlxButton);
export { HlxButton };
//# sourceMappingURL=button.js.map